(() => {
  if (window.__AB_PRODUCT_VIDEO_AUTOPLAY_V2__) return;
  window.__AB_PRODUCT_VIDEO_AUTOPLAY_V2__ = true;

  const VIDEO_SELECTOR = '.productView-video[data-mp4] video, .productView-video.mp4 video';
  const videos = new Set();
  const stateMap = new WeakMap();

  const ensureStyles = () => {
    if (document.getElementById('ab-product-video-autoplay-style')) return;

    const style = document.createElement('style');
    style.id = 'ab-product-video-autoplay-style';
    style.textContent = `
      .productView-video { position: relative; }
      .ab-video-fallback-play {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 8;
        width: 54px;
        height: 54px;
        min-width: 54px;
        min-height: 54px;
        padding: 0;
        border: 1px solid #d6c08a;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.58);
        transform: translate(-50%, -50%);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .ab-video-fallback-play[hidden] { display: none !important; }
      .ab-video-fallback-play::before {
        content: '';
        display: block;
        width: 0;
        height: 0;
        margin: 0 auto;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        border-left: 13px solid #d6c08a;
        transform: translateX(2px);
      }
    `;
    document.head.appendChild(style);
  };

  const getState = (video) => {
    let state = stateMap.get(video);

    if (!state) {
      state = {
        visible: false,
        blocked: false,
        fallback: null,
        bound: false,
        observed: false,
        retryTimer: null
      };
      stateMap.set(video, state);
    }

    return state;
  };

  const hideFallback = (video) => {
    const state = getState(video);
    if (state.fallback) state.fallback.hidden = true;
  };

  const ensureFallback = (video) => {
    const state = getState(video);
    if (state.fallback && state.fallback.isConnected) return state.fallback;

    const host = video.closest('.productView-video') || video.parentElement;
    if (!host) return null;

    let button = host.querySelector('.ab-video-fallback-play');

    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'ab-video-fallback-play';
      button.setAttribute('aria-label', 'Play product video');
      button.hidden = true;
      host.appendChild(button);
    }

    if (!button.dataset.abBound) {
      button.dataset.abBound = '1';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const currentVideo = host.querySelector('video') || video;
        const currentState = getState(currentVideo);
        currentState.blocked = false;
        playVideo(currentVideo, true);
      });
    }

    state.fallback = button;
    return button;
  };

  const showFallback = (video) => {
    const state = getState(video);
    if (!state.visible || document.hidden) return;

    const button = ensureFallback(video);
    if (button) button.hidden = false;
  };

  const applyAutoplayMode = (video) => {
    video.controls = false;
    video.removeAttribute('controls');

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');

    video.autoplay = true;
    video.setAttribute('autoplay', '');

    video.loop = true;
    video.setAttribute('loop', '');

    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    if ('disablePictureInPicture' in video) video.disablePictureInPicture = true;
    if ('disableRemotePlayback' in video) video.disableRemotePlayback = true;
  };

  const playVideo = async (video, userGesture = false) => {
    const state = getState(video);

    if (document.hidden || !state.visible) return;
    if (state.blocked && !userGesture) return;

    applyAutoplayMode(video);

    try {
      const result = video.play();
      if (result && typeof result.then === 'function') await result;

      state.blocked = false;
      hideFallback(video);
    } catch (error) {
      state.blocked = true;
      showFallback(video);
    }
  };

  const pauseVideo = (video) => {
    const state = getState(video);
    state.blocked = false;
    hideFallback(video);

    if (state.retryTimer) {
      window.clearTimeout(state.retryTimer);
      state.retryTimer = null;
    }

    if (!video.paused) video.pause();
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const state = getState(video);

          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.55;
          applyAutoplayMode(video);

          if (state.visible) {
            state.blocked = false;
            playVideo(video);
          } else {
            pauseVideo(video);
          }
        });
      }, { threshold: [0, 0.55, 1] })
    : null;

  const bindVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;

    videos.add(video);
    const state = getState(video);
    applyAutoplayMode(video);

    if (!state.bound) {
      state.bound = true;

      video.addEventListener('play', () => hideFallback(video));

      video.addEventListener('pause', () => {
        const current = getState(video);

        if (!current.visible || document.hidden || current.blocked) return;

        if (current.retryTimer) window.clearTimeout(current.retryTimer);
        current.retryTimer = window.setTimeout(() => {
          current.retryTimer = null;
          playVideo(video);
        }, 100);
      });
    }

    if (observer && !state.observed) {
      state.observed = true;
      observer.observe(video);
    } else if (!observer) {
      state.visible = true;
      playVideo(video);
    }
  };

  const scan = (root) => {
    if (!root) return;

    if (root instanceof HTMLVideoElement && root.matches(VIDEO_SELECTOR)) {
      bindVideo(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(VIDEO_SELECTOR).forEach(bindVideo);
    }
  };

  ensureStyles();
  scan(document);

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      });
    });
  });

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener('visibilitychange', () => {
    videos.forEach((video) => {
      const state = getState(video);

      if (document.hidden) {
        pauseVideo(video);
      } else if (state.visible) {
        state.blocked = false;
        playVideo(video);
      }
    });
  });
})();
